import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Activity, CheckCircle2, Clock, Building2, Users, FileSignature, Wrench } from "lucide-react";

const SERVICE_LABELS = {
  carwash: "Carwash",
  used_car_detail: "Used Car Detail",
  porter: "Porter",
  bodyshop: "Bodyshop",
  onboarding: "Onboarding",
};

function StatTile({ icon: Icon, label, value, accent = "black", testid }) {
  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-caps text-zinc-500">{label}</div>
        <Icon size={18} className={accent === "red" ? "text-[#FF2B00]" : "text-black"} />
      </div>
      <div className="font-display text-5xl tracking-tighter mt-3">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => { setStats(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 lg:p-12" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="label-caps text-[#FF2B00]">Welcome back, {user?.name?.split(" ")[0]}</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Operations Overview</h1>
        </div>
        <div className="hidden md:block label-caps text-zinc-500">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
      </div>

      {loading ? (
        <div className="text-zinc-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <StatTile icon={Activity} label="Active Jobs" value={stats?.services?.in_progress ?? 0} testid="stat-active" accent="red" />
            <StatTile icon={Clock} label="Pending" value={stats?.services?.pending ?? 0} testid="stat-pending" />
            <StatTile icon={CheckCircle2} label="Completed" value={stats?.services?.completed ?? 0} testid="stat-completed" />
            <StatTile icon={FileSignature} label="Pending Sigs" value={stats?.signatures?.pending ?? 0} testid="stat-signatures" accent="red" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-zinc-200 p-6" data-testid="service-breakdown">
              <div className="label-caps text-zinc-500 mb-6">Service Breakdown</div>
              <div className="space-y-4">
                {Object.entries(stats?.services?.by_type || {}).map(([key, count]) => {
                  const total = Math.max(stats.services.total, 1);
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium">{SERVICE_LABELS[key]}</span>
                        <span className="label-caps text-zinc-500">{count}</span>
                      </div>
                      <div className="h-2 bg-zinc-100">
                        <div className="h-full bg-black" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-black text-white p-6" data-testid="employees-tile">
                <Users size={20} className="text-[#FF2B00]" />
                <div className="font-display text-4xl mt-4">{stats?.employees?.total ?? 0}</div>
                <div className="label-caps text-zinc-400 mt-1">Employees</div>
                <div className="text-sm text-zinc-300 mt-3">{stats?.employees?.onboarding_active ?? 0} actively onboarding</div>
              </div>
              <div className="bg-white border border-zinc-200 p-6" data-testid="dealerships-tile">
                <Building2 size={20} className="text-[#002FA7]" />
                <div className="font-display text-4xl mt-4">{stats?.dealerships?.total ?? 0}</div>
                <div className="label-caps text-zinc-500 mt-1">Dealership Clients</div>
              </div>
              <div className="bg-[#FF2B00] text-white p-6" data-testid="ai-cta-tile">
                <Wrench size={20} />
                <div className="font-display text-2xl mt-4 leading-tight">Need help prioritizing?</div>
                <div className="text-sm mt-2 opacity-90">Tap the EAG Copilot for actionable workflow guidance.</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
